/**
 * eprTemplateService.ts
 *
 * Firestore CRUD for hr_v1 EPR framework templates (`eprTemplates`
 * collection) plus the snapshot builder used at launch.
 *
 * Distinct from reviewTemplateService.ts, which serves the legacy OPTICS
 * path (`reviewTemplates`). Do not merge the two — CLAUDE.md §K.
 *
 * Template lifecycle:
 *   - The default template is seeded once from the §C constants and is the
 *     canonical framework form (scale 1–5, weights 30/40/20/10).
 *   - HR edits bump `templateVersion`; live reviews are unaffected because
 *     every launch stamps an immutable EPRTemplateSnapshot on the
 *     submission (the form reads the snapshot, never the live template).
 *   - Changing scaleConfig or weights away from the §C defaults is a
 *     CLAUDE.md §I change — HR sign-off required before such a template
 *     may be used for a pay-path review.
 */

import {
  collection, doc, getDoc, getDocs, setDoc, addDoc, updateDoc,
  query, where, serverTimestamp, increment,
  type UpdateData, type DocumentData,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import type {
  EPRFrameworkTemplate, EPRTemplateSnapshot, EPRScaleConfig,
  ComponentSource, EPRComponent,
} from '../types/eprFramework';
import {
  CORE_COMPETENCIES, LEADERSHIP_SKILLS,
  KPI_SCALE, OKR_SCALE, VALUE_SCALE,
  DEFAULT_WEIGHTS_PEOPLE_MANAGER,
  DEFAULT_SCALE_CONFIG, DEFAULT_COMPONENT_SOURCES,
} from '../types/eprFramework';
import { omitUndefinedDeep } from './firestoreUtils';

const COL = 'eprTemplates';

/** Stable id so the seed is idempotent and easy to reference. */
export const DEFAULT_EPR_TEMPLATE_ID = 'default_hr_v1';

// ─── Resolvers (template field → effective value) ───────────────────────────

export function resolveScaleConfig(t?: Pick<EPRFrameworkTemplate, 'scaleConfig'> | null): EPRScaleConfig {
  return t?.scaleConfig ?? DEFAULT_SCALE_CONFIG;
}

export function resolveComponentSources(
  t?: Pick<EPRFrameworkTemplate, 'componentSources'> | null,
): Record<EPRComponent, ComponentSource> {
  return { ...DEFAULT_COMPONENT_SOURCES, ...(t?.componentSources ?? {}) };
}

// ─── Seed ────────────────────────────────────────────────────────────────────

function defaultTemplateData(createdBy: string): Omit<EPRFrameworkTemplate, 'id' | 'createdAt'> {
  return {
    name: 'Rascal EPR — Performance Review Framework',
    version: 'hr_v1',
    weights: DEFAULT_WEIGHTS_PEOPLE_MANAGER,
    coreCompetencies: CORE_COMPETENCIES,
    leadershipSkills: LEADERSHIP_SKILLS,
    scoringScales: { kpi: KPI_SCALE, okr: OKR_SCALE, value: VALUE_SCALE },
    appliesToLevels: ['general', 'supervisor', 'dept_head'],
    scaleConfig: DEFAULT_SCALE_CONFIG,
    componentSources: DEFAULT_COMPONENT_SOURCES,
    status: 'active',
    templateVersion: 1,
    createdBy,
  };
}

/**
 * Idempotent — creates the canonical default template if absent, returns
 * it either way. Call from the HR review-creation page on mount.
 */
export async function ensureDefaultEprTemplate(createdBy: string): Promise<EPRFrameworkTemplate> {
  const ref = doc(db, COL, DEFAULT_EPR_TEMPLATE_ID);
  const snap = await getDoc(ref);
  if (snap.exists()) {
    return { id: snap.id, ...(snap.data() as Omit<EPRFrameworkTemplate, 'id'>) };
  }
  await setDoc(ref, {
    ...defaultTemplateData(createdBy),
    createdAt: serverTimestamp(),
  });
  const created = await getDoc(ref);
  return { id: created.id, ...(created.data() as Omit<EPRFrameworkTemplate, 'id'>) };
}

// ─── CRUD ────────────────────────────────────────────────────────────────────

export async function getEprTemplate(id: string): Promise<EPRFrameworkTemplate | null> {
  const snap = await getDoc(doc(db, COL, id));
  if (!snap.exists()) return null;
  return { id: snap.id, ...(snap.data() as Omit<EPRFrameworkTemplate, 'id'>) };
}

/** Active templates only — the list offered on the Create Review screen. */
export async function listActiveEprTemplates(): Promise<EPRFrameworkTemplate[]> {
  const q = query(collection(db, COL), where('status', '==', 'active'));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...(d.data() as Omit<EPRFrameworkTemplate, 'id'>) }));
}

export type EprTemplateInput = Omit<EPRFrameworkTemplate, 'id' | 'createdAt' | 'updatedAt' | 'templateVersion'>;

export async function createEprTemplate(data: EprTemplateInput): Promise<string> {
  const ref = await addDoc(collection(db, COL), omitUndefinedDeep({
    ...data,
    templateVersion: 1,
    createdAt: serverTimestamp(),
  }));
  return ref.id;
}

/**
 * Every edit bumps templateVersion. Launched reviews keep rendering from
 * their snapshot; the bump only affects reviews created after the edit.
 */
export async function updateEprTemplate(
  id: string,
  data: Partial<EprTemplateInput>,
): Promise<void> {
  // omitUndefinedDeep widens the payload to a plain record; narrow it
  // back to the SDK's update type (UpdateData) rather than a structural
  // Record cast, which newer firebase typings reject.
  await updateDoc(doc(db, COL, id), omitUndefinedDeep({
    ...data,
    templateVersion: increment(1),
    updatedAt: serverTimestamp(),
  }) as UpdateData<DocumentData>);
}

export async function archiveEprTemplate(id: string): Promise<void> {
  await updateDoc(doc(db, COL, id), {
    status: 'archived',
    updatedAt: serverTimestamp(),
  });
}

// ─── Snapshot builder (launch-time) ─────────────────────────────────────────

/**
 * Freeze the template into the shape the magic-link form reads. Stamped on
 * every submission at launch so template edits never mutate live or
 * historical forms.
 */
export function buildTemplateSnapshot(t: EPRFrameworkTemplate): EPRTemplateSnapshot {
  return {
    templateId: t.id,
    templateName: t.name,
    ...(typeof t.templateVersion === 'number' ? { templateVersion: t.templateVersion } : {}),
    scaleConfig: resolveScaleConfig(t),
    componentSources: resolveComponentSources(t),
    scoringScales: { kpi: t.scoringScales.kpi, okr: t.scoringScales.okr },
    coreCompetencies: t.coreCompetencies,
    leadershipSkills: t.leadershipSkills,
  };
}
